import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { BookingExtrasApi } from './api';

/**
 * What BP-25 §6's screens need from outside React.
 *
 * **Four capabilities, and the third is the unusual one.** Looking is
 * `scheduling.read`; changing a booking is `scheduling.write`; spending
 * a deposit on a bill is `billing.write`; and *keeping* a client's
 * money is owner-or-manager, because ARCHITECTURE §3 Auth has no string
 * that means it and BP-28 set the precedent. The api checks every one
 * of them again — a gate in the browser is a courtesy, never the
 * control (BP-04 R14).
 */
export interface BookingExtrasDeps {
  api: BookingExtrasApi;
  /** `scheduling.read`: the waitlist, a booking's deposit, a repeat. */
  canSee: boolean;
  /** `scheduling.write`: ask for a deposit, join or clear the waitlist. */
  canSchedule: boolean;
  /** `billing.write`: apply a paid deposit to the bill at check-out. */
  canBill: boolean;
  /** Owner or manager: keep a client's deposit after a no-show. */
  canForfeit: boolean;
  /** `settings.manage`: the five numbers and the RwG feeds. */
  canManageSettings: boolean;
  /** BP-01 R16: a read-only tenant reads everything and writes nothing. */
  writable: boolean;
  /** A fresh `Idempotency-Key` per button press (ARCHITECTURE §3 API). */
  newIdempotencyKey: () => string;
  /** Now, injected so a countdown does not depend on the machine's clock. */
  now: () => Date;
}

/**
 * A random `Idempotency-Key`; `crypto.randomUUID` exists in every target
 * browser. Named rather than inlined at the composition root, the shape
 * `features/expenses` set: an arrow written there is a function the
 * staff app's own 100 % floor would have to reach, and the thing worth
 * testing is that two calls differ.
 */
export function randomIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * The wall clock, for the offer countdown.
 *
 * Injected for the same reason `features/expenses` injects the date: a
 * test that read the machine's clock would pass at 10:00 and fail at
 * 10:31, and the one place that reads it for real is this line.
 */
export function nowDate(): Date {
  return new Date();
}

const BookingExtrasContext = createContext<BookingExtrasDeps | null>(null);

/** Provide the booking-extras dependencies to a subtree. */
export function BookingExtrasProvider({
  deps,
  children,
}: {
  deps: BookingExtrasDeps;
  children: ReactNode;
}): JSX.Element {
  return <BookingExtrasContext.Provider value={deps}>{children}</BookingExtrasContext.Provider>;
}

/**
 * Read them back.
 *
 * Throws rather than defaulting: a screen rendered outside the provider
 * would otherwise tell a manager she may not do things she may.
 */
export function useBookingExtrasDeps(): BookingExtrasDeps {
  const deps = useContext(BookingExtrasContext);
  if (deps === null) {
    throw new Error('useBookingExtrasDeps must be used inside a <BookingExtrasProvider>');
  }
  return deps;
}
